import { ptBrCommon } from '@/lib/pt-br/messages';
import { useCreateProductAvailabilityNotification } from '@/framework/useProductAvailabilityNotifications';
import Button from '@/components/ui/button';
import Input from '@/components/ui/forms/input';
import { LoginUserInput } from '@/types';
import { Image } from '@/components/ui/image';
import { Resolver, SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import Bell from '@/assets/icons/bell.svg';
import { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { userAtom } from '@/framework/user-store';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { yieldToMain } from '@/lib/yield-to-main';

const emailFormValidation = yup.object().shape({
  hasEmail: yup.boolean().default(false),
  email: yup
    .string()
    .email('error-email-format')
    .when('hasEmail', (hasEmail, schema) =>
      hasEmail ? schema.required('error-email-required') : schema.notRequired(),
    ),
});

type StockStep = 'email' | 'confirmation';

const EmailForm = ({
  email,
  onSubmit,
  isLoading,
}: {
  email?: string;
  onSubmit: (v: any) => void;
  isLoading?: boolean;
  serverError?: any;
}) => {
  const {
    register,
    reset,
    formState: { errors },
    handleSubmit,
  } = useForm<{
    email: string;
    hasEmail: boolean;
  }>({
    defaultValues: { email: email ?? '', hasEmail: !!email },
    resolver: yupResolver(emailFormValidation) as unknown as Resolver<
      { email: string; hasEmail: boolean },
      any
    >,
  });

  useEffect(() => {
    reset({
      email: email ?? '',
      hasEmail: !!email,
    });
  }, [email, reset]);

  return (
    <form
      onSubmit={handleSubmit((data) => onSubmit(data))}
      className="text-left w-full"
    >
      <div className="flex flex-col items-center gap-4">
        {!email && (
          <Input
            className="w-full"
            inputClassName="border-none"
            dimension="small"
            type="email"
            autoComplete="email"
            spellCheck={false}
            placeholder="Insira seu e-mail"
            {...register('email')}
            error={ptBrCommon(errors.email?.message!)}
          />
        )}
        <Button
          type="submit"
          className="w-4-12 h-[34px] text-sm tracking-[0.2px] rounded-3xl"
          loading={isLoading}
          disabled={isLoading}
        >
          <Image src={Bell} alt="" width={14} height={18} unoptimized={true} />
          <span className="ml-1">Avise-me!</span>
        </Button>
      </div>
    </form>
  );
};

function BoxConfirmation() {
  return (
    <div className="bg-[#F3F4F6] py-2 rounded text-center">
      <h4 className="text-accent text-[14px] font-semibold">
        Estamos combinados :)
      </h4>
      <p className="text-gray-200 text-[11px]">
        Avisaremos assim que o produto estiver disponível novamente.
      </p>
    </div>
  );
}

const MissingProduct = ({ productId }: { productId: number }) => {
  const [step, setStep] = useState<StockStep>('email');
  const [user] = useAtom(userAtom);
  const { mutate, isLoading } = useCreateProductAvailabilityNotification();

  const emailFormHandle: SubmitHandler<Pick<LoginUserInput, 'email'>> = ({
    email,
  }) => {
    if (isLoading) return;

    const notifyEmail = (user?.email || email || '').trim();
    if (!notifyEmail) return;

    mutate(
      { email: notifyEmail, product_id: productId },
      {
        onSuccess: () => {
          void (async () => {
            await yieldToMain();
            setStep('confirmation');
          })();
        },
        onError: () => {
          void (async () => {
            await yieldToMain();
            toast.error(
              'Não foi possível registrar o aviso. Tente novamente.',
            );
          })();
        },
      },
    );
  };

  return (
    <div className="w-full">
      {step === 'email' && (
        <EmailForm
          email={user?.email}
          onSubmit={emailFormHandle}
          isLoading={isLoading}
        />
      )}

      {step === 'confirmation' && <BoxConfirmation />}
    </div>
  );
};

export default MissingProduct;
